import React, {FC, lazy, Suspense, useCallback, useEffect, useMemo, useState} from "react";
import {useTranslation} from "react-i18next";
import {Link, useLocation, useNavigate} from "react-router-dom";
import type {ITenantCreating, ITenantFormLayout} from "@ib-tehnologije/react-widgets";
import dayjs from "dayjs";
import {
    ArchivedEventReturnDtoSortInput,
    ArchivedEventsQuery,
    EventReturnDtoSortInput,
    EventsPageEventsQuery,
    EventsPageEventsQueryVariables,
    SortEnumType,
    useActivateEventActionMutation,
    useArchiveEventActionMutation,
    useArchivedEventsQuery,
    useCompleteEventActionMutation,
    useCreateEventMutation,
    useDuplicateEventActionMutation,
    useEventsPageEventsQuery,
    useRestoreEventActionMutation,
    useSaveEventBasicSetupFromWizardMutation
} from "../../GraphQL/generated/graphql";
import type {EventReturnDto} from "../../GraphQL/generated/graphql";
import {Alert, App, Button, Descriptions, Drawer, Empty, Grid, Input, Popconfirm, Space, Table, Typography} from "antd";
import type {TableColumnsType} from "antd";
import {ArrowRightOutlined, CheckOutlined, CopyOutlined, FolderOpenOutlined, InboxOutlined, PlusOutlined, RollbackOutlined} from "@ant-design/icons";
import {
    eventOperationalStatusTone,
    formatEventDateRange,
    getArchivedEventDisplayName,
    getEventDisplayName,
    getEventOperationalStatus,
    type EventOperationalStatus
} from "./eventsPageHelpers";
import {eventWorkspaceRoute} from "./eventWorkspaceRoutes";
import {
    buildEventBasicSetupSaveInputFromCreateValues,
    extractCreatedEventId
} from "./eventCreationWizardHelpers";
import type {EventCreationWizardValues} from "./eventCreationWizardHelpers";
import {buildEventCreateDto} from "./eventCreatePayload";
import type {EventCreateFormData} from "./eventCreatePayload";
import {buildUserFacingErrorLabels, getUserFacingErrorMessage} from "../../userFacingErrorUtils";
import "../../Styles/EventsPage.css";
import {formatLocalDate, formatLocalDateTime} from "./adminDateFormat";
import {StatusTag} from "../../Components/ui/StatusTag";
import {AdminPage, PageHeader} from "../../Components/ui";

const EVENTS_PAGE_SIZE = 25;
const ARCHIVED_EVENTS_PAGE_SIZE = 25;
const EventCreationWizard = lazy(() => import("./EventCreationWizard"));

type EventsPageEvent = NonNullable<NonNullable<EventsPageEventsQuery["events"]>["items"]>[number];

type ArchivedEvent = NonNullable<NonNullable<ArchivedEventsQuery["archivedEvents"]>["items"]>[number];

type EventSearchWhere = NonNullable<EventsPageEventsQueryVariables["where"]>;

type EventCreatingConfigDependencies = {
    createEvent: (options: any) => Promise<any>;
    eventsQuery: {
        refetch: () => Promise<unknown> | unknown;
    };
    formDefinition: ITenantFormLayout<EventCreateFormData>;
};

type EventsPageView = "active" | "archived";

type EventsPageProps = {
    view?: EventsPageView;
};

type EventTableSortColumn = "identity" | "operationalStatus" | "dateRange" | "participantCount" | "reservationCount";
type EventTableSortOrder = "ascend" | "descend";
type EventTableSortState = {
    columnKey: EventTableSortColumn;
    order: EventTableSortOrder;
} | null;
type EventGraphqlSortDirection = SortEnumType;

const operationalStatusDefaults: Record<EventOperationalStatus, {label: string}> = {
    draft: {
        label: "Draft setup"
    },
    active: {
        label: "Active"
    },
    completed: {
        label: "Completed"
    }
};

const eventTableSortColumns = new Set<EventTableSortColumn>([
    "identity",
    "operationalStatus",
    "dateRange",
    "participantCount",
    "reservationCount"
]);

const toGraphqlSortDirection = (order: EventTableSortOrder): EventGraphqlSortDirection =>
    order === "ascend" ? SortEnumType.Asc : SortEnumType.Desc;

const buildEventOrder = (sortState: EventTableSortState): EventReturnDtoSortInput[] => {
    if (!sortState) {
        return [{id: SortEnumType.Desc}];
    }

    const direction = toGraphqlSortDirection(sortState.order);
    switch (sortState.columnKey) {
    case "identity":
        return [{name: direction}, {id: SortEnumType.Desc}];
    case "operationalStatus":
        return [{lifecycleStatus: direction}, {id: SortEnumType.Desc}];
    case "dateRange":
        return [{startDate: direction}, {endDate: direction}, {id: SortEnumType.Desc}];
    case "participantCount":
        return [{participantCount: direction}, {id: SortEnumType.Desc}];
    case "reservationCount":
        return [{reservationCount: direction}, {id: SortEnumType.Desc}];
    default:
        return [{id: SortEnumType.Desc}];
    }
};

const buildArchivedEventOrder = (sortState: EventTableSortState): ArchivedEventReturnDtoSortInput[] => {
    if (!sortState) {
        return [{archivedAt: SortEnumType.Desc}, {id: SortEnumType.Desc}];
    }

    return buildEventOrder(sortState);
};

const resolveEventTableSortState = (sorter: {
    columnKey?: React.Key;
    order?: EventTableSortOrder | null;
} | Array<{
    columnKey?: React.Key;
    order?: EventTableSortOrder | null;
}>): EventTableSortState => {
    const resolvedSorter = Array.isArray(sorter) ? sorter[0] : sorter;
    const columnKey = resolvedSorter?.columnKey;

    if (typeof columnKey !== "string" || !eventTableSortColumns.has(columnKey as EventTableSortColumn)) {
        return null;
    }

    if (resolvedSorter.order !== "ascend" && resolvedSorter.order !== "descend") {
        return null;
    }

    return {
        columnKey: columnKey as EventTableSortColumn,
        order: resolvedSorter.order
    };
};

export const buildEventsPageCreatingConfig = ({
    createEvent,
    eventsQuery,
    formDefinition
}: EventCreatingConfigDependencies): ITenantCreating<any, EventReturnDto, EventCreateFormData, "input"> => ({
    fn: async (options: any) => {
        const result = await createEvent(options);
        await eventsQuery.refetch();
        return result;
    },
    modelKey: "input",
    baseFormData: {
        code: "",
        name: "",
        nameHr: "",
        nameEn: "",
        place: "",
        address: "",
        description: "",
        eventInformation: "",
        eventInformationHr: "",
        eventInformationEn: "",
        onSiteRegistrationInstructions: "",
        onSiteRegistrationInstructionsHr: "",
        onSiteRegistrationInstructionsEn: "",
        hasParticipationFee: true,
        startDate: dayjs().startOf("day").toDate(),
        endDate: dayjs().add(1, "day").startOf("day").toDate()
    },
    formDefinition,
    formDataToDto: (values) => buildEventCreateDto(values as EventCreateFormData)
});

const normalizeLifecycleStatus = (value?: string | null) => value?.trim().toUpperCase() ?? "";

const isCompletedEvent = (item: Pick<EventsPageEvent, "lifecycleStatus">) =>
    normalizeLifecycleStatus(item.lifecycleStatus) === "COMPLETED";

const isActiveEvent = (item: Pick<EventsPageEvent, "lifecycleStatus">) =>
    normalizeLifecycleStatus(item.lifecycleStatus) === "ACTIVE";

const extractReturnedEventId = (payload: unknown): number | null => {
    if (!Array.isArray(payload)) {
        return null;
    }

    const firstItem = payload[0];
    return typeof firstItem?.id === "number" ? firstItem.id : null;
};

const buildDuplicateEventInput = (event: EventsPageEvent) => {
    const suffix = dayjs().format("YYYYMMDDHHmmss");
    const fallbackName = getEventDisplayName(event);
    const nameHr = event.nameHr?.trim() || event.name?.trim() || event.nameEn?.trim() || fallbackName;
    const nameEn = event.nameEn?.trim() || event.name?.trim() || event.nameHr?.trim() || fallbackName;

    return {
        sourceEventId: event.id,
        code: `${event.code.trim() || `EVENT-${event.id}`}-COPY-${suffix}`,
        name: `${fallbackName} copy`,
        nameHr: `${nameHr} kopija`,
        nameEn: `${nameEn} copy`,
        startDate: event.startDate || dayjs().startOf("day").toISOString(),
        endDate: event.endDate || event.startDate || dayjs().add(1, "day").startOf("day").toISOString()
    };
};

export const EventsPage: FC<EventsPageProps> = ({view = "active"}) => {
    const {t} = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const {message} = App.useApp();
    const errorLabels = buildUserFacingErrorLabels(t);
    const screens = Grid.useBreakpoint();
    const useMobileEventRows = screens.xs && !screens.md;
    const isArchivedView = view === "archived";
    const [activePage, setActivePage] = useState(1);
    const [activeEventSort, setActiveEventSort] = useState<EventTableSortState>(null);
    const [selectedActiveEventId, setSelectedActiveEventId] = useState<number | null>(null);
    const [eventSearchInput, setEventSearchInput] = useState("");
    const [eventSearch, setEventSearch] = useState("");
    const [archivedPage, setArchivedPage] = useState(1);
    const [archivedEventSort, setArchivedEventSort] = useState<EventTableSortState>(null);
    const [selectedArchivedEvent, setSelectedArchivedEvent] = useState<ArchivedEvent | null>(null);
    const [createWizardOpen, setCreateWizardOpen] = useState(false);
    const [pendingActionKey, setPendingActionKey] = useState<string | null>(null);
    const [createEvent, {loading: creatingEvent}] = useCreateEventMutation();
    const [saveWizardBasicSetup, {loading: savingWizardBasicSetup}] = useSaveEventBasicSetupFromWizardMutation();
    const [duplicateEvent] = useDuplicateEventActionMutation();
    const [completeEvent] = useCompleteEventActionMutation();
    const [activateEvent] = useActivateEventActionMutation();
    const [archiveEvent] = useArchiveEventActionMutation();
    const [restoreEvent] = useRestoreEventActionMutation();
    const creating = creatingEvent || savingWizardBasicSetup;

    useEffect(() => {
        if (view !== "active") {
            return;
        }

        if (new URLSearchParams(location.search).get("view") === "archived") {
            navigate("/Admin/Events/Archive", {replace: true});
        }
    }, [location.search, navigate, view]);

    const activeWhere = useMemo<EventSearchWhere | undefined>(() => {
        const search = eventSearch.trim();

        if (!search) {
            return undefined;
        }

        return {
            or: [
                {name: {contains: search}},
                {nameHr: {contains: search}},
                {nameEn: {contains: search}},
                {code: {contains: search}},
                {description: {contains: search}},
                {place: {contains: search}}
            ]
        };
    }, [eventSearch]);
    const activeEventOrder = useMemo(() => buildEventOrder(activeEventSort), [activeEventSort]);
    const archivedEventOrder = useMemo(() => buildArchivedEventOrder(archivedEventSort), [archivedEventSort]);
    const activeEventsQuery = useEventsPageEventsQuery({
        variables: {
            take: EVENTS_PAGE_SIZE,
            skip: (activePage - 1) * EVENTS_PAGE_SIZE,
            order: activeEventOrder,
            ...(activeWhere ? {where: activeWhere} : {})
        },
        fetchPolicy: "cache-and-network",
        notifyOnNetworkStatusChange: true
    });
    const archivedEventsQuery = useArchivedEventsQuery({
        variables: {
            take: ARCHIVED_EVENTS_PAGE_SIZE,
            skip: (archivedPage - 1) * ARCHIVED_EVENTS_PAGE_SIZE,
            order: archivedEventOrder
        },
        fetchPolicy: "cache-and-network",
        notifyOnNetworkStatusChange: true
    });
    const handleCreateFromWizard = useCallback(async (values: EventCreationWizardValues) => {
        try {
            const createResult = await createEvent({
                variables: {
                    input: buildEventCreateDto(values)
                }
            });
            const createdEventId = extractCreatedEventId(createResult);

            if (!createdEventId) {
                throw new Error(t("event-create-wizard.missing-event-id", {
                    defaultValue: "Event was created, but the new workspace could not be identified."
                }));
            }

            await saveWizardBasicSetup({
                variables: {
                    input: buildEventBasicSetupSaveInputFromCreateValues(createdEventId, values)
                }
            });
            await activeEventsQuery.refetch();
            setCreateWizardOpen(false);
            message.success(t("event-create-wizard.created", {defaultValue: "Event created"}));
            navigate(eventWorkspaceRoute(createdEventId));
        } catch (error: any) {
            message.error(getUserFacingErrorMessage(error, t, errorLabels));
        }
    }, [activeEventsQuery, createEvent, message, errorLabels, navigate, saveWizardBasicSetup, t]);

    const isActionPending = useCallback((action: string, eventId: number) =>
        pendingActionKey === `${action}:${eventId}`, [pendingActionKey]);

    const handleDuplicateEvent = useCallback(async (item: EventsPageEvent) => {
        const actionKey = `duplicate:${item.id}`;
        setPendingActionKey(actionKey);

        try {
            const result = await duplicateEvent({
                variables: {
                    input: buildDuplicateEventInput(item)
                }
            });
            const duplicatedEventId = extractReturnedEventId(result.data?.cloneEvent);

            if (!duplicatedEventId) {
                throw new Error(t("events-page.duplicate-missing-event-id", {
                    defaultValue: "The duplicated event was created, but the new workspace could not be opened."
                }));
            }

            await activeEventsQuery.refetch();
            message.success(t("events-page.duplicated", {defaultValue: "Event duplicated"}));
            navigate(eventWorkspaceRoute(duplicatedEventId));
        } catch (error: any) {
            message.error(getUserFacingErrorMessage(error, t, errorLabels));
        } finally {
            setPendingActionKey(current => current === actionKey ? null : current);
        }
    }, [activeEventsQuery, duplicateEvent, message, errorLabels, navigate, t]);

    const handleCompleteEvent = useCallback(async (item: EventsPageEvent) => {
        const actionKey = `complete:${item.id}`;
        setPendingActionKey(actionKey);

        try {
            await completeEvent({
                variables: {
                    input: {id: item.id}
                }
            });
            await activeEventsQuery.refetch();
            message.success(t("events-page.completed", {defaultValue: "Event marked complete"}));
        } catch (error: any) {
            message.error(getUserFacingErrorMessage(error, t, errorLabels));
        } finally {
            setPendingActionKey(current => current === actionKey ? null : current);
        }
    }, [activeEventsQuery, completeEvent, message, errorLabels, t]);

    const handleActivateEvent = useCallback(async (item: EventsPageEvent) => {
        const actionKey = `activate:${item.id}`;
        const wasCompleted = isCompletedEvent(item);
        setPendingActionKey(actionKey);

        try {
            await activateEvent({
                variables: {
                    input: {id: item.id}
                }
            });
            await activeEventsQuery.refetch();
            message.success(wasCompleted
                ? t("events-page.reactivated", {defaultValue: "Event returned to active status"})
                : t("events-page.activated", {defaultValue: "Event marked active"}));
        } catch (error: any) {
            message.error(getUserFacingErrorMessage(error, t, errorLabels));
        } finally {
            setPendingActionKey(current => current === actionKey ? null : current);
        }
    }, [activateEvent, activeEventsQuery, message, errorLabels, t]);

    const handleArchiveEvent = useCallback(async (item: EventsPageEvent) => {
        const actionKey = `archive:${item.id}`;
        setPendingActionKey(actionKey);

        try {
            await archiveEvent({
                variables: {
                    input: {id: item.id}
                }
            });
            await Promise.all([
                activeEventsQuery.refetch(),
                archivedEventsQuery.refetch()
            ]);
            message.success(t("events-page.archived", {defaultValue: "Event archived"}));
        } catch (error: any) {
            message.error(getUserFacingErrorMessage(error, t, errorLabels));
        } finally {
            setPendingActionKey(current => current === actionKey ? null : current);
        }
    }, [activeEventsQuery, archiveEvent, archivedEventsQuery, message, errorLabels, t]);

    const handleRestoreEvent = useCallback(async (item: ArchivedEvent) => {
        const actionKey = `restore:${item.id}`;
        setPendingActionKey(actionKey);

        try {
            await restoreEvent({
                variables: {
                    input: {id: item.id}
                }
            });
            await Promise.all([
                activeEventsQuery.refetch(),
                archivedEventsQuery.refetch()
            ]);
            setSelectedArchivedEvent(current => current?.id === item.id ? null : current);
            message.success(t("events-page.restored", {defaultValue: "Event restored"}));
        } catch (error: any) {
            message.error(getUserFacingErrorMessage(error, t, errorLabels));
        } finally {
            setPendingActionKey(current => current === actionKey ? null : current);
        }
    }, [activeEventsQuery, archivedEventsQuery, message, errorLabels, restoreEvent, t]);

    const activeEvents = useMemo(() => (activeEventsQuery.data?.events?.items ?? [])
        .filter((item): item is EventsPageEvent => !!item), [activeEventsQuery.data?.events?.items]);
    const archivedEvents = useMemo(() => (archivedEventsQuery.data?.archivedEvents?.items ?? [])
        .filter((item): item is ArchivedEvent => !!item), [archivedEventsQuery.data?.archivedEvents?.items]);
    const selectedActiveEvent = useMemo(() =>
        activeEvents.find(item => item.id === selectedActiveEventId) ?? null, [activeEvents, selectedActiveEventId]);
    const activeEventTotal = activeEventsQuery.data?.events?.totalCount ?? activeEvents.length;
    const archivedEventTotal = archivedEventsQuery.data?.archivedEvents?.totalCount ?? archivedEvents.length;

    const handleActiveSearch = useCallback((value: string) => {
        setEventSearch(value.trim());
        setActivePage(1);
    }, []);

    const handleActiveTableChange = useCallback((_: unknown, __: unknown, sorter: Parameters<typeof resolveEventTableSortState>[0]) => {
        setActiveEventSort(resolveEventTableSortState(sorter));
        setActivePage(1);
    }, []);

    const handleArchivedTableChange = useCallback((_: unknown, __: unknown, sorter: Parameters<typeof resolveEventTableSortState>[0]) => {
        setArchivedEventSort(resolveEventTableSortState(sorter));
        setArchivedPage(1);
    }, []);

    const activeColumnSortOrder = useCallback((columnKey: EventTableSortColumn) =>
        activeEventSort?.columnKey === columnKey ? activeEventSort.order : undefined, [activeEventSort]);

    const archivedColumnSortOrder = useCallback((columnKey: EventTableSortColumn) =>
        archivedEventSort?.columnKey === columnKey ? archivedEventSort.order : undefined, [archivedEventSort]);

    const clearActiveSearch = useCallback(() => {
        setEventSearch("");
        setEventSearchInput("");
        setActivePage(1);
    }, []);

    const toggleActiveEventSelection = useCallback((eventId: number) => {
        setSelectedActiveEventId(current => current === eventId ? null : eventId);
    }, []);

    useEffect(() => {
        if (!selectedActiveEventId) {
            return;
        }

        if (!activeEvents.some(item => item.id === selectedActiveEventId)) {
            setSelectedActiveEventId(null);
        }
    }, [activeEvents, selectedActiveEventId]);

    const renderOperationalStatus = useCallback((item: EventsPageEvent) => {
        const status = getEventOperationalStatus(item);
        const defaults = operationalStatusDefaults[status];

        return (
            <Space className="events-page__status-cell">
                <StatusTag tone={eventOperationalStatusTone(status)}>
                    {t(`event-operational-status.${status}`, {defaultValue: defaults.label})}
                </StatusTag>
            </Space>
        );
    }, [t]);

    const renderActiveEventIdentity = useCallback((item: EventsPageEvent) => (
        <Space direction="vertical" size={4} className="events-page__event-identity">
            <Link
                className="events-page__event-name events-page__event-name--link"
                onClick={event => event.stopPropagation()}
                to={eventWorkspaceRoute(item.id)}
            >
                {getEventDisplayName(item)}
            </Link>
            {item.place?.trim() ? (
                <Typography.Text type="secondary" className="events-page__event-place">
                    {item.place.trim()}
                </Typography.Text>
            ) : null}
            <Space size={8} wrap className="events-page__event-meta">
                <Typography.Text type="secondary" className="events-page__event-mobile-meta">
                    {formatEventDateRange(item.startDate, item.endDate)}
                </Typography.Text>
            </Space>
        </Space>
    ), []);

    const renderOpenWorkspaceAction = useCallback((item: EventsPageEvent) => (
        <Button
            icon={<ArrowRightOutlined />}
            type="primary"
            onClick={() => navigate(eventWorkspaceRoute(item.id))}
        >
            {t("event-workspace.open", {defaultValue: "Open"})}
        </Button>
    ), [navigate, t]);

    const renderCountValue = useCallback((value?: number | null) => {
        if (typeof value !== "number" || Number.isNaN(value) || value < 0) {
            return 0;
        }

        return value;
    }, []);

    const activeColumns = useMemo<TableColumnsType<EventsPageEvent>>(() => {
        if (useMobileEventRows) {
            return [
                {
                    key: "identity",
                    title: t("event", {defaultValue: "Event"}),
                    sorter: true,
                    sortOrder: activeColumnSortOrder("identity"),
                    render: (_, item) => (
                        <Space direction="vertical" size={10} className="events-page__event-identity events-page__event-identity--mobile">
                            {renderActiveEventIdentity(item)}
                            {renderOperationalStatus(item)}
                            <Typography.Text type="secondary">
                                {t("participants", {defaultValue: "Participants"})}: {renderCountValue(item.participantCount)}
                                {" | "}
                                {t("reservations", {defaultValue: "Reservations"})}: {renderCountValue(item.reservationCount)}
                            </Typography.Text>
                            <div className="events-page__mobile-action">
                                {renderOpenWorkspaceAction(item)}
                            </div>
                        </Space>
                    )
                }
            ];
        }

        return [
            {
                key: "identity",
                title: t("event", {defaultValue: "Event"}),
                width: 360,
                sorter: true,
                sortOrder: activeColumnSortOrder("identity"),
                render: (_, item) => renderActiveEventIdentity(item)
            },
            {
                key: "operationalStatus",
                title: t("status", {defaultValue: "Status"}),
                width: 220,
                sorter: true,
                sortOrder: activeColumnSortOrder("operationalStatus"),
                render: (_, item) => renderOperationalStatus(item)
            },
            {
                key: "dateRange",
                title: t("date-range", {defaultValue: "Date range"}),
                width: 210,
                responsive: ["md"],
                sorter: true,
                sortOrder: activeColumnSortOrder("dateRange"),
                render: (_, item) => formatEventDateRange(item.startDate, item.endDate)
            },
            {
                key: "participantCount",
                title: t("participants", {defaultValue: "Participants"}),
                width: 140,
                align: "center",
                responsive: ["md"],
                sorter: true,
                sortOrder: activeColumnSortOrder("participantCount"),
                render: (_, item) => renderCountValue(item.participantCount)
            },
            {
                key: "reservationCount",
                title: t("reservations", {defaultValue: "Reservations"}),
                width: 140,
                align: "center",
                responsive: ["md"],
                sorter: true,
                sortOrder: activeColumnSortOrder("reservationCount"),
                render: (_, item) => renderCountValue(item.reservationCount)
            },
            {
                key: "actions",
                title: t("actions", {defaultValue: "Actions"}),
                width: 260,
                align: "right",
                render: (_, item) => renderOpenWorkspaceAction(item)
            }
        ];
    }, [activeColumnSortOrder, renderActiveEventIdentity, renderCountValue, renderOpenWorkspaceAction, renderOperationalStatus, t, useMobileEventRows]);

    const renderArchivedEventIdentity = useCallback((item: ArchivedEvent) => (
        <Space direction="vertical" size={4} className="events-page__event-identity">
            <Typography.Text strong className="events-page__event-name">
                {getArchivedEventDisplayName(item)}
            </Typography.Text>
            {item.place?.trim() ? (
                <Typography.Text type="secondary" className="events-page__event-place">
                    {item.place.trim()}
                </Typography.Text>
            ) : null}
            <Space size={8} wrap className="events-page__event-meta">
                <Typography.Text type="secondary" className="events-page__event-mobile-meta">
                    {formatEventDateRange(item.startDate, item.endDate)}
                </Typography.Text>
            </Space>
        </Space>
    ), []);

    const renderArchivedStatus = useCallback((item: ArchivedEvent) => (
        <Space direction="vertical" size={2} className="events-page__status-cell">
            <StatusTag tone={eventOperationalStatusTone(getEventOperationalStatus(item))}>
                {t(`event-operational-status.${getEventOperationalStatus(item)}`, {
                    defaultValue: operationalStatusDefaults[getEventOperationalStatus(item)].label
                })}
            </StatusTag>
            <Typography.Text type="secondary" className="events-page__status-hint">
                {t("archived-at", {defaultValue: "Archived at"})}: {formatLocalDateTime(item.archivedAt)}
            </Typography.Text>
        </Space>
    ), [t]);

    const renderArchivedEventActions = useCallback((item: ArchivedEvent) => (
        <Space wrap>
            <Button
                icon={<RollbackOutlined />}
                loading={isActionPending("restore", item.id)}
                onClick={() => handleRestoreEvent(item)}
            >
                {t("events-page.restore", {defaultValue: "Restore"})}
            </Button>
            <Button icon={<FolderOpenOutlined />} onClick={() => setSelectedArchivedEvent(item)}>
                {t("review", {defaultValue: "Review"})}
            </Button>
        </Space>
    ), [handleRestoreEvent, isActionPending, t]);

    const archivedColumns = useMemo<TableColumnsType<ArchivedEvent>>(() => {
        if (useMobileEventRows) {
            return [
                {
                    key: "identity",
                    title: t("event", {defaultValue: "Event"}),
                    sorter: true,
                    sortOrder: archivedColumnSortOrder("identity"),
                    render: (_, item) => (
                        <Space direction="vertical" size={10} className="events-page__event-identity events-page__event-identity--mobile">
                            {renderArchivedEventIdentity(item)}
                            {renderArchivedStatus(item)}
                            <Typography.Text type="secondary">
                                {t("participants", {defaultValue: "Participants"})}: {renderCountValue(item.participantCount)}
                                {" | "}
                                {t("reservations", {defaultValue: "Reservations"})}: {renderCountValue(item.reservationCount)}
                            </Typography.Text>
                            <div className="events-page__mobile-action">
                                {renderArchivedEventActions(item)}
                            </div>
                        </Space>
                    )
                }
            ];
        }

        return [
            {
                key: "identity",
                title: t("event", {defaultValue: "Event"}),
                width: 360,
                sorter: true,
                sortOrder: archivedColumnSortOrder("identity"),
                render: (_, item) => renderArchivedEventIdentity(item)
            },
            {
                key: "operationalStatus",
                title: t("status", {defaultValue: "Status"}),
                width: 220,
                sorter: true,
                sortOrder: archivedColumnSortOrder("operationalStatus"),
                render: (_, item) => renderArchivedStatus(item)
            },
            {
                key: "dateRange",
                title: t("date-range", {defaultValue: "Date range"}),
                width: 210,
                responsive: ["md"],
                sorter: true,
                sortOrder: archivedColumnSortOrder("dateRange"),
                render: (_, item) => formatEventDateRange(item.startDate, item.endDate)
            },
            {
                key: "participantCount",
                title: t("participants", {defaultValue: "Participants"}),
                width: 140,
                align: "center",
                responsive: ["md"],
                sorter: true,
                sortOrder: archivedColumnSortOrder("participantCount"),
                render: (_, item) => renderCountValue(item.participantCount)
            },
            {
                key: "reservationCount",
                title: t("reservations", {defaultValue: "Reservations"}),
                width: 140,
                align: "center",
                responsive: ["md"],
                sorter: true,
                sortOrder: archivedColumnSortOrder("reservationCount"),
                render: (_, item) => renderCountValue(item.reservationCount)
            },
            {
                key: "actions",
                title: t("actions", {defaultValue: "Actions"}),
                width: 420,
                align: "right",
                render: (_, item) => renderArchivedEventActions(item)
            }
        ];
    }, [archivedColumnSortOrder, renderArchivedEventActions, renderArchivedEventIdentity, renderArchivedStatus, renderCountValue, t, useMobileEventRows]);

    const activeEmptyState = eventSearch ? (
        <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={t("events-page.no-search-results", {defaultValue: "No events match this search."})}
        >
            <Button onClick={clearActiveSearch}>
                {t("clear-search", {defaultValue: "Clear search"})}
            </Button>
        </Empty>
    ) : (
        <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={t("events-page.empty-active", {defaultValue: "No events are ready for selection yet."})}
        >
            <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateWizardOpen(true)}>
                {t("cta.create-event", {defaultValue: "Create event"})}
            </Button>
        </Empty>
    );

    const archivedEmptyState = (
        <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={t("events-page.empty-archive", {defaultValue: "No archived events yet."})}
        />
    );

    return (
        <AdminPage>
            <PageHeader
                title={isArchivedView
                    ? t("events-page.archive-title", {defaultValue: "Archive"})
                    : t("events", {defaultValue: "Events"})}
                description={isArchivedView
                    ? t("archived-events-count", {
                        defaultValue: "{{count}} archived events",
                        count: archivedEventTotal
                    })
                    : t("events-description", {defaultValue: "Create and manage events, schedules, and published access codes."})}
                actions={isArchivedView ? undefined : (
                    <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateWizardOpen(true)}>
                        {t("cta.create-event", {defaultValue: "Create event"})}
                    </Button>
                )}
            />
            <div className="events-page">

                {isArchivedView ? (
                    <Space direction="vertical" size={16} className="events-page__view">
                        {archivedEventsQuery.error ? (
                            <Alert
                                description={t("events-page.archive-error-description", {
                                    defaultValue: "Refresh the archive or try again later."
                                })}
                                message={t("events-page.archive-error", {defaultValue: "Archive unavailable"})}
                                showIcon
                                type="error"
                            />
                        ) : null}
                        <Table
                            className="events-page__table"
                            columns={archivedColumns}
                            dataSource={archivedEvents}
                            loading={archivedEventsQuery.loading}
                            locale={{emptyText: archivedEmptyState}}
                            onChange={handleArchivedTableChange}
                            pagination={{
                                current: archivedPage,
                                pageSize: ARCHIVED_EVENTS_PAGE_SIZE,
                                total: archivedEventTotal,
                                showSizeChanger: false,
                                onChange: nextPage => setArchivedPage(nextPage)
                            }}
                            rowKey="id"
                            scroll={useMobileEventRows ? undefined : {x: 1280}}
                        />
                    </Space>
                ) : (
                    <Space direction="vertical" size={16} className="events-page__view">
                        <section className="events-page__toolbar">
                            <div className="events-page__toolbar-copy">
                                <Typography.Text strong>
                                    {t("events-page.active-title", {defaultValue: "Events available for selection"})}
                                </Typography.Text>
                                <Typography.Text type="secondary">
                                    {t("events-page.active-count", {
                                        defaultValue: "{{count}} events",
                                        count: activeEventTotal
                                    })}
                                </Typography.Text>
                            </div>
                            <Space wrap className="events-page__toolbar-actions">
                                <Button
                                    icon={<CopyOutlined />}
                                    disabled={!selectedActiveEvent}
                                    loading={selectedActiveEvent ? isActionPending("duplicate", selectedActiveEvent.id) : false}
                                    onClick={() => selectedActiveEvent && handleDuplicateEvent(selectedActiveEvent)}
                                >
                                    {t("events-page.duplicate", {defaultValue: "Duplicate"})}
                                </Button>
                                {selectedActiveEvent && !isActiveEvent(selectedActiveEvent) ? (
                                    <Popconfirm
                                        title={isCompletedEvent(selectedActiveEvent)
                                            ? t("events-page.reactivate-confirm-title", {defaultValue: "Reactivate this event?"})
                                            : t("events-page.activate-confirm-title", {defaultValue: "Mark this event active?"})}
                                        description={isCompletedEvent(selectedActiveEvent)
                                            ? t("events-page.reactivate-confirm-description", {
                                                defaultValue: "The event returns to active operations and public registration can become available again."
                                            })
                                            : t("events-page.activate-confirm-description", {
                                                defaultValue: "The event becomes operational. Public registration still requires an active registration form."
                                            })}
                                        okText={isCompletedEvent(selectedActiveEvent)
                                            ? t("events-page.reactivate", {defaultValue: "Reactivate"})
                                            : t("events-page.mark-active", {defaultValue: "Mark active"})}
                                        cancelText={t("cancel", {defaultValue: "Cancel"})}
                                        onConfirm={() => handleActivateEvent(selectedActiveEvent)}
                                    >
                                        <Button
                                            icon={isCompletedEvent(selectedActiveEvent) ? <RollbackOutlined /> : <CheckOutlined />}
                                            loading={isActionPending("activate", selectedActiveEvent.id)}
                                        >
                                            {isCompletedEvent(selectedActiveEvent)
                                                ? t("events-page.reactivate", {defaultValue: "Reactivate"})
                                                : t("events-page.mark-active", {defaultValue: "Mark active"})}
                                        </Button>
                                    </Popconfirm>
                                ) : (
                                    <Popconfirm
                                        title={t("events-page.complete-confirm-title", {defaultValue: "Mark this event complete?"})}
                                        description={t("events-page.complete-confirm-description", {
                                            defaultValue: "The event stays available here and certificate generation becomes available for finished operations."
                                        })}
                                        okText={t("events-page.mark-complete", {defaultValue: "Mark complete"})}
                                        cancelText={t("cancel", {defaultValue: "Cancel"})}
                                        disabled={!selectedActiveEvent}
                                        onConfirm={() => selectedActiveEvent && handleCompleteEvent(selectedActiveEvent)}
                                    >
                                        <Button
                                            disabled={!selectedActiveEvent}
                                            icon={<CheckOutlined />}
                                            loading={selectedActiveEvent ? isActionPending("complete", selectedActiveEvent.id) : false}
                                        >
                                            {t("events-page.mark-complete", {defaultValue: "Mark complete"})}
                                        </Button>
                                    </Popconfirm>
                                )}
                                <Popconfirm
                                    title={t("events-page.archive-confirm-title", {defaultValue: "Archive this event?"})}
                                    description={t("events-page.archive-confirm-description", {
                                        defaultValue: "This event leaves active selection and stays available in archive and history."
                                    })}
                                    okText={t("events-page.archive-action", {defaultValue: "Archive event"})}
                                    cancelText={t("cancel", {defaultValue: "Cancel"})}
                                    disabled={!selectedActiveEvent}
                                    onConfirm={() => selectedActiveEvent && handleArchiveEvent(selectedActiveEvent)}
                                >
                                    <Button
                                        danger
                                        disabled={!selectedActiveEvent}
                                        icon={<InboxOutlined />}
                                        loading={selectedActiveEvent ? isActionPending("archive", selectedActiveEvent.id) : false}
                                    >
                                        {t("events-page.archive-action", {defaultValue: "Archive event"})}
                                    </Button>
                                </Popconfirm>
                                <Space.Compact className="events-page__search">
                                    <Input
                                        allowClear
                                        onChange={(event) => {
                                            const value = event.target.value;
                                            setEventSearchInput(value);

                                            if (!value.trim()) {
                                                setEventSearch("");
                                                setActivePage(1);
                                            }
                                        }}
                                        onPressEnter={() => handleActiveSearch(eventSearchInput)}
                                        placeholder={t("events-page.search-placeholder", {defaultValue: "Search events"})}
                                        value={eventSearchInput}
                                    />
                                    <Button onClick={() => handleActiveSearch(eventSearchInput)}>
                                        {t("search", {defaultValue: "Search"})}
                                    </Button>
                                </Space.Compact>
                            </Space>
                        </section>
                        {activeEventsQuery.error ? (
                            <Alert
                                description={t("events-page.active-error-description", {
                                    defaultValue: "Refresh the list or try again later."
                                })}
                                message={t("events-page.active-error", {defaultValue: "Events unavailable"})}
                                showIcon
                                type="error"
                            />
                        ) : null}
                        <Table
                            className="events-page__table"
                            columns={activeColumns}
                            dataSource={activeEvents}
                            loading={activeEventsQuery.loading || creating}
                            locale={{emptyText: activeEmptyState}}
                            onChange={handleActiveTableChange}
                            pagination={{
                                current: activePage,
                                pageSize: EVENTS_PAGE_SIZE,
                                total: activeEventTotal,
                                showSizeChanger: false,
                                onChange: nextPage => setActivePage(nextPage)
                            }}
                            onRow={(item) => ({
                                onClick: () => toggleActiveEventSelection(item.id)
                            })}
                            rowClassName={(item) => item.id === selectedActiveEventId ? "events-page__active-row--selected" : ""}
                            rowSelection={{
                                type: "radio",
                                selectedRowKeys: selectedActiveEvent ? [selectedActiveEvent.id] : [],
                                onChange: (selectedRowKeys) => {
                                    const selectedKey = selectedRowKeys[0];
                                    if (typeof selectedKey === "number") {
                                        toggleActiveEventSelection(selectedKey);
                                        return;
                                    }

                                    setSelectedActiveEventId(null);
                                }
                            }}
                            rowKey="id"
                            scroll={useMobileEventRows ? undefined : {x: 1280}}
                        />
                    </Space>
                )}
            </div>
            <Drawer
                title={selectedArchivedEvent ? getArchivedEventDisplayName(selectedArchivedEvent) : t("archived-event", {defaultValue: "Archived event"})}
                open={!!selectedArchivedEvent}
                onClose={() => setSelectedArchivedEvent(null)}
                width="min(560px, 100vw)"
                destroyOnHidden
            >
                {selectedArchivedEvent && (
                    <div className="ati-stack">
                        <Descriptions bordered column={1} size="small">
                            <Descriptions.Item label={t("archive-state", {defaultValue: "Archive state"})}>
                                <StatusTag>{t("archived", {defaultValue: "Archived"})}</StatusTag>
                            </Descriptions.Item>
                            <Descriptions.Item label={t("code", {defaultValue: "Code"})}>
                                <Typography.Text code>{selectedArchivedEvent.code}</Typography.Text>
                            </Descriptions.Item>
                            <Descriptions.Item label={t("name", {defaultValue: "Name"})}>
                                {getArchivedEventDisplayName(selectedArchivedEvent)}
                            </Descriptions.Item>
                            <Descriptions.Item label={t("name-hr", {defaultValue: "Name (Croatian)"})}>
                                {selectedArchivedEvent.nameHr?.trim() || "-"}
                            </Descriptions.Item>
                            <Descriptions.Item label={t("name-en", {defaultValue: "Name (English)"})}>
                                {selectedArchivedEvent.nameEn?.trim() || "-"}
                            </Descriptions.Item>
                            <Descriptions.Item label={t("place", {defaultValue: "Place"})}>
                                {selectedArchivedEvent.place?.trim() || "-"}
                            </Descriptions.Item>
                            <Descriptions.Item label={t("start-date", {defaultValue: "Start date"})}>
                                {formatLocalDate(selectedArchivedEvent.startDate)}
                            </Descriptions.Item>
                            <Descriptions.Item label={t("end-date", {defaultValue: "End date"})}>
                                {formatLocalDate(selectedArchivedEvent.endDate)}
                            </Descriptions.Item>
                            <Descriptions.Item label={t("operational-status-before-archive", {defaultValue: "Operational status before archive"})}>
                                <StatusTag tone={eventOperationalStatusTone(getEventOperationalStatus(selectedArchivedEvent))}>
                                    {t(`event-operational-status.${getEventOperationalStatus(selectedArchivedEvent)}`, {
                                        defaultValue: operationalStatusDefaults[getEventOperationalStatus(selectedArchivedEvent)].label
                                    })}
                                </StatusTag>
                            </Descriptions.Item>
                            <Descriptions.Item label={t("archived-at", {defaultValue: "Archived at"})}>
                                {formatLocalDateTime(selectedArchivedEvent.archivedAt)}
                            </Descriptions.Item>
                        </Descriptions>
                    </div>
                )}
            </Drawer>
            {createWizardOpen ? (
                <Suspense fallback={null}>
                    <EventCreationWizard
                        open={createWizardOpen}
                        loading={creating}
                        onCancel={() => setCreateWizardOpen(false)}
                        onCreate={handleCreateFromWizard}
                    />
                </Suspense>
            ) : null}
        </AdminPage>
    );
};

export default EventsPage;
